/*
 * Gallery Locator — native Stockist replacement.
 *
 * Two modes, chosen automatically:
 *   MAP  mode  — when a Google Maps key is set AND at least one gallery has lat/lng.
 *                Google Maps + Places autocomplete + geometry + MarkerClusterer,
 *                live area search, distance sort, info windows. Full Stockist parity.
 *   LIST mode  — fallback when no coordinates exist yet (before backend geocodes).
 *                A searchable text list of every gallery. Keeps the page usable.
 *
 * No jQuery. Reads window.GALLERY_LOCATIONS + window.GALLERY_LOCATOR_CONFIG.
 */
(function () {
  'use strict';

  var CFG = window.GALLERY_LOCATOR_CONFIG || {};
  var DATA = Array.isArray(window.GALLERY_LOCATIONS) ? window.GALLERY_LOCATIONS : [];
  var CLUSTERER_SRC = 'https://unpkg.com/@googlemaps/markerclusterer@2.5.3/dist/index.min.js';

  /* ------------------------------------------------------------------ helpers */
  function num(v) { var n = parseFloat(v); return isFinite(n) ? n : null; }
  function hasLatLng(g) { return num(g.lat) !== null && num(g.lng) !== null; }
  function nz(s) { return s != null && String(s).trim() !== ''; }
  function esc(s) { var d = document.createElement('div'); d.textContent = (s == null ? '' : String(s)); return d.innerHTML; }
  function telHref(p) { return 'tel:' + String(p).replace(/[^0-9+]/g, ''); }

  function fullAddress(g) {
    return [g.address, g.city, g.state, g.postal].filter(nz).join(', ');
  }
  function directionsUrl(g) {
    var dest = hasLatLng(g)
      ? (num(g.lat) + ',' + num(g.lng))
      : [fullAddress(g), g.country].filter(nz).join(', ');
    return 'https://www.google.com/maps/dir/?api=1&destination=' + encodeURIComponent(dest);
  }

  /* Normalize field aliases so both data sources share one shape. */
  DATA.forEach(function (g, i) {
    if (g.postal == null && g.postal_code != null) g.postal = g.postal_code;
    if (g.address == null && g.address1 != null) g.address = g.address1;
    if (g.state == null && g.province != null) g.state = g.province;
    if (g.lat == null && g.latitude != null) g.lat = g.latitude;
    if (g.lng == null && g.longitude != null) g.lng = g.longitude;
    g.__idx = i;
  });

  var els = {};
  function grab() {
    els.wrap = document.getElementById('gallery-locator');
    els.input = document.getElementById('gl-search-input');
    els.btn = document.getElementById('gl-search-btn');
    els.panel = document.getElementById('gl-panel');
    els.results = document.getElementById('gl-results');
    els.count = document.getElementById('gl-count');
    els.empty = document.getElementById('gl-empty');
    els.map = document.getElementById('gl-map');
    els.reset = document.getElementById('gl-reset-btn');
  }

  function applyTheme() {
    if (CFG.featureColor && els.wrap) els.wrap.style.setProperty('--gl-feature', CFG.featureColor);
  }

  function setCount(n) {
    if (els.count) els.count.textContent = (n === 1 ? '1 gallery' : n + ' galleries');
  }
  function showEmpty(show) {
    if (els.empty) els.empty.hidden = !show;
  }

  var PIN_SVG = '<svg viewBox="0 0 24 24" width="18" height="22" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true">' +
    '<path d="M12 22s-7-6.5-7-12a7 7 0 0 1 14 0c0 5.5-7 12-7 12z"></path><circle cx="12" cy="10" r="2.4"></circle></svg>';

  /* Shared result card — matches the Stockist layout:
     pin icon + bold name + multi-line address (street / City, ST Zip / Country) + stacked phone & website. */
  function cardHtml(g, distKm) {
    var addrLines = [];
    if (nz(g.address)) addrLines.push(esc(g.address));
    var cityLine = [[g.city, g.state].filter(nz).join(', '), g.postal].filter(nz).join(' ');
    if (nz(cityLine)) addrLines.push(esc(cityLine));
    if (nz(g.country)) addrLines.push(esc(g.country));

    var links = [];
    if (nz(g.phone)) links.push('<a class="gl-card-link" href="' + esc(telHref(g.phone)) + '">' + esc(g.phone) + '</a>');
    if (nz(g.website)) links.push('<a class="gl-card-link" href="' + esc(g.website) + '" target="_blank" rel="noopener">Visit Website</a>');

    var dist = (distKm != null && isFinite(distKm)) ? '<span class="gl-card-dist">' + Math.round(distKm) + ' km</span>' : '';

    return '<li class="gl-card" data-idx="' + g.__idx + '">' +
      '<span class="gl-card-pin">' + PIN_SVG + dist + '</span>' +
      '<div class="gl-card-body">' +
        '<div class="gl-card-name">' + esc(g.name) + '</div>' +
        (addrLines.length ? '<div class="gl-card-addr">' + addrLines.join('<br>') + '</div>' : '') +
        (links.length ? '<div class="gl-card-links">' + links.join('') + '</div>' : '') +
      '</div>' +
    '</li>';
  }

  /* ============================================================= LIST MODE */
  function initListMode() {
    if (els.wrap) els.wrap.classList.add('gl-mode-list');

    var sorted = DATA.slice().sort(function (a, b) {
      return String(a.name || '').localeCompare(String(b.name || ''));
    });

    function render(list) {
      els.results.innerHTML = list.map(function (g) { return cardHtml(g); }).join('');
      setCount(list.length);
      showEmpty(list.length === 0);
    }

    function filter() {
      var q = (els.input.value || '').trim().toLowerCase();
      if (!q) { render(sorted); return; }
      render(sorted.filter(function (g) {
        return [g.name, g.city, g.state, g.postal, g.country, g.address].some(function (v) {
          return nz(v) && String(v).toLowerCase().indexOf(q) !== -1;
        });
      }));
    }

    render(sorted);
    els.input.addEventListener('input', filter);
    els.btn.addEventListener('click', filter);
    els.input.addEventListener('keydown', function (e) { if (e.key === 'Enter') { e.preventDefault(); filter(); } });
    if (els.reset) els.reset.addEventListener('click', function () { els.input.value = ''; render(sorted); });
  }

  /* ============================================================== MAP MODE */
  var map, markers = [], clusterer = null, infoWindow, geometry, allBounds = null;
  var searchCenter = null; // set on a typed LOCATION search; cleared when the user drags
  var textFilter = null;   // set when searching by gallery name/text; null = browse all
  var selectedMarker = null; // the one gallery currently shown as a red pin
  var scrollTopPending = false; // snap the list to top when fresh search results render

  function initMapMode() {
    if (!(window.google && google.maps)) { initListMode(); return; }
    if (els.wrap) els.wrap.classList.add('gl-mode-map');
    geometry = google.maps.geometry && google.maps.geometry.spherical;

    map = new google.maps.Map(els.map, {
      zoom: 4,
      center: { lat: 39.5, lng: -98.35 },
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: true,
      clickableIcons: false
    });
    infoWindow = new google.maps.InfoWindow();
    infoWindow.addListener('closeclick', function () {
      if (selectedMarker) { markerToCircle(selectedMarker); selectedMarker = null; }
    });

    var bounds = new google.maps.LatLngBounds();
    DATA.filter(hasLatLng).forEach(function (g) {
      var pos = { lat: num(g.lat), lng: num(g.lng) };
      var marker = new google.maps.Marker({ position: pos, title: g.name, icon: circleMarkerIcon(34), label: circleMarkerLabel() });
      marker.__idx = g.__idx;
      marker.addListener('click', function () { selectMarker(g, marker); });
      markers.push(marker);
      bounds.extend(pos);
    });

    allBounds = bounds;
    setupClusterer();
    if (!bounds.isEmpty()) map.fitBounds(bounds);

    map.addListener('idle', refreshList);
    map.addListener('dragstart', function () { searchCenter = null; textFilter = null; });

    setupSearch();
    refreshList();
  }

  /* One shared circle look for both cluster bubbles and single galleries:
     a big solid black disc with a thin translucent halo ring (matches the reference). */
  function circleUrl() {
    var color = CFG.clusterColor || '#333333';
    var svg = '<svg xmlns="http://www.w3.org/2000/svg" width="70" height="70" fill="' + color + '">'
      + '<circle cx="35" cy="35" r="35" opacity="0.2"/>'
      + '<circle cx="35" cy="35" r="28" opacity="1"/></svg>';
    return 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg);
  }
  function circleMarkerIcon(size) {
    return { url: circleUrl(), scaledSize: new google.maps.Size(size, size), anchor: new google.maps.Point(size / 2, size / 2) };
  }
  function circleMarkerLabel() {
    return { text: '1', color: '#ffffff', fontSize: '11px', fontWeight: '600' };
  }
  function markerToCircle(marker) { marker.setIcon(circleMarkerIcon(34)); marker.setLabel(circleMarkerLabel()); }
  function markerToPin(marker) { marker.setIcon(null); marker.setLabel(null); } // null icon = Google's red pin
  function selectMarker(g, marker) {
    if (selectedMarker && selectedMarker !== marker) markerToCircle(selectedMarker);
    selectedMarker = marker;
    markerToPin(marker);
    openInfo(g, marker);
    highlightCard(g.__idx);
  }

  function setupClusterer() {
    function build() {
      if (!(window.markerClusterer && window.markerClusterer.MarkerClusterer)) return false;
      var renderer = {
        render: function (cluster) {
          var count = cluster.count;
          var size = 46 + Math.min(count, 300) / 12;
          return new google.maps.Marker({
            position: cluster.position,
            icon: {
              url: circleUrl(),
              scaledSize: new google.maps.Size(size, size),
              anchor: new google.maps.Point(size / 2, size / 2)
            },
            label: { text: String(count), color: '#ffffff', fontSize: '13px', fontWeight: '600' },
            zIndex: 1000 + count
          });
        }
      };
      var algorithm = window.markerClusterer.SuperClusterAlgorithm
        ? new window.markerClusterer.SuperClusterAlgorithm({ maxZoom: CFG.clusterMaxZoom || 8, minPoints: 1, radius: 150 })
        : undefined;
      clusterer = new window.markerClusterer.MarkerClusterer({
        map: map, markers: markers, renderer: renderer, algorithm: algorithm
      });
      return true;
    }
    if (build()) return;
    // Load the clustering library on demand; if it fails, fall back to plain pins.
    loadScript(CLUSTERER_SRC, function () {
      if (!build()) markers.forEach(function (m) { m.setMap(map); });
    }, function () {
      markers.forEach(function (m) { m.setMap(map); });
    });
  }

  function openInfo(g, marker) {
    var addrLines = [];
    if (nz(g.address)) addrLines.push(esc(g.address));
    var cityLine = [[g.city, g.state].filter(nz).join(', '), g.postal].filter(nz).join(' ');
    if (nz(cityLine)) addrLines.push(esc(cityLine));
    if (nz(g.country)) addrLines.push(esc(g.country));

    var links = [];
    if (nz(g.website)) links.push('<a href="' + esc(g.website) + '" target="_blank" rel="noopener">Visit Website</a>');
    links.push('<a href="' + directionsUrl(g) + '" target="_blank" rel="noopener">' + esc(CFG.directionsLabel || 'Directions') + '</a>');

    infoWindow.setContent(
      '<div class="gl-iw">' +
      '<div class="gl-iw-name">' + esc(g.name) + '</div>' +
      (addrLines.length ? '<div class="gl-iw-addr">' + addrLines.join('<br>') + '</div>' : '') +
      (nz(g.phone) ? '<div class="gl-iw-phone"><a href="' + esc(telHref(g.phone)) + '">' + esc(g.phone) + '</a></div>' : '') +
      '<div class="gl-iw-links">' + links.join('') + '</div>' +
      '</div>'
    );
    infoWindow.open(map, marker);
  }

  function refreshList() {
    var b = map.getBounds();
    if (!b) return;
    var center = searchCenter || b.getCenter();
    var minRadius = (CFG.minSearchKm || 50) * 1000;
    var halfDiag = geometry ? geometry.computeDistanceBetween(b.getNorthEast(), b.getSouthWest()) / 2 : minRadius;
    var radius = searchCenter ? Math.max(minRadius, halfDiag) : halfDiag;

    var rows = ( textFilter || DATA ).filter(hasLatLng).map(function (g) {
      var pos = new google.maps.LatLng(num(g.lat), num(g.lng));
      return {
        g: g,
        d: geometry ? geometry.computeDistanceBetween(center, pos) : 0,
        inView: b.contains(pos)
      };
    });

    var list = searchCenter
      ? rows.filter(function (r) { return r.d <= radius; })
      : rows.filter(function (r) { return r.inView; });

    list.sort(function (a, b2) { return a.d - b2.d; });
    var max = CFG.maxResults || 100;
    if (list.length > max) list = list.slice(0, max);

    // Display the (nearest-capped) set in alphabetical order by name.
    list.sort(function (a, b2) { return String(a.g.name || '').localeCompare(String(b2.g.name || '')); });

    els.results.innerHTML = list.map(function (r) { return cardHtml(r.g, searchCenter ? r.d / 1000 : null); }).join('');
    setCount(list.length);
    showEmpty(list.length === 0);
    bindCardClicks();
    if (selectedMarker) {
      var sc = els.results.querySelector('.gl-card[data-idx="' + selectedMarker.__idx + '"]');
      if (sc) sc.classList.add('is-active');
    }
    if (scrollTopPending) { if (els.panel) els.panel.scrollTop = 0; scrollTopPending = false; }
  }

  function bindCardClicks() {
    Array.prototype.forEach.call(els.results.querySelectorAll('.gl-card'), function (card) {
      card.addEventListener('click', function (e) {
        if (e.target.closest('a')) return; // links do their own thing
        var idx = parseInt(card.getAttribute('data-idx'), 10);
        var marker = markers.filter(function (m) { return m.__idx === idx; })[0];
        var g = DATA[idx];
        if (marker && g) {
          map.panTo(marker.getPosition());
          if (map.getZoom() < 10) map.setZoom(12);
          selectMarker(g, marker);
        }
      });
    });
  }

  function highlightCard(idx) {
    Array.prototype.forEach.call(els.results.querySelectorAll('.gl-card'), function (c) { c.classList.remove('is-active'); });
    var card = els.results.querySelector('.gl-card[data-idx="' + idx + '"]');
    if (card) { card.classList.add('is-active'); card.scrollIntoView({ block: 'nearest' }); }
  }

  // Reset the map to the initial fit-all view, as if freshly loaded.
  function resetMap() {
    els.input.value = '';
    textFilter = null;
    searchCenter = null;
    if (selectedMarker) { markerToCircle(selectedMarker); selectedMarker = null; }
    if (infoWindow) infoWindow.close();
    if (allBounds && !allBounds.isEmpty()) map.fitBounds(allBounds);
    refreshList();
  }

  function setupSearch() {
    var hasPlaces = google.maps.places && google.maps.places.Autocomplete;
    var placeSelected = false;
    if (hasPlaces) {
      var ac = new google.maps.places.Autocomplete(els.input, { fields: ['geometry'] });
      ac.addListener('place_changed', function () {
        var place = ac.getPlace();
        if (place && place.geometry) {
          placeSelected = true;
          applyPlace(place.geometry);
          setTimeout(function () { placeSelected = false; }, 80);
        } else {
          runSearch();
        }
      });
    }
    els.btn.addEventListener('click', function () { runSearch(); });
    if (els.reset) els.reset.addEventListener('click', resetMap);
    els.input.addEventListener('keydown', function (e) {
      if (e.key !== 'Enter') return;
      // If Places just resolved a geographic place, keep it; otherwise search galleries by name.
      setTimeout(function () { if (!placeSelected) runSearch(); }, hasPlaces ? 80 : 0);
    });
  }

  // Match galleries by name/city/state/postal/address — every typed word must appear.
  function searchGalleries(q) {
    var tokens = q.toLowerCase().split(/\s+/).filter(Boolean);
    if (!tokens.length) return [];
    return DATA.filter(function (g) {
      var hay = [g.name, g.city, g.state, g.postal, g.address, g.country].filter(nz).join(' ').toLowerCase();
      return tokens.every(function (t) { return hay.indexOf(t) !== -1; });
    });
  }

  // Unified search: gallery name/text first; fall back to geographic geocoding only if nothing matches.
  function runSearch() {
    var q = (els.input.value || '').trim();
    if (!q) return;
    scrollTopPending = true;
    var matches = searchGalleries(q);
    if (matches.length) {
      textFilter = matches;
      searchCenter = null;
      var coded = matches.filter(hasLatLng);
      if (coded.length === 1) {
        var g0 = coded[0];
        map.setCenter(new google.maps.LatLng(num(g0.lat), num(g0.lng)));
        if (map.getZoom() < 12) map.setZoom(13);
        var m0 = markers.filter(function (m) { return m.__idx === g0.__idx; })[0];
        if (m0) selectMarker(g0, m0);
      } else if (coded.length > 1) {
        var b = new google.maps.LatLngBounds();
        coded.forEach(function (g) { b.extend(new google.maps.LatLng(num(g.lat), num(g.lng))); });
        map.fitBounds(b);
      }
      refreshList();
    } else {
      textFilter = null;
      geocodeSearch();
    }
  }

  function findNearest(latLng) {
    if (!geometry) return null;
    var best = null, bestD = Infinity;
    DATA.filter(hasLatLng).forEach(function (g) {
      var d = geometry.computeDistanceBetween(latLng, new google.maps.LatLng(num(g.lat), num(g.lng)));
      if (d < bestD) { bestD = d; best = g; }
    });
    return best;
  }

  function applyPlace(geom) {
    scrollTopPending = true;
    textFilter = null;
    searchCenter = geom.location;
    // Surface the nearest gallery to the searched place as a selected red pin + open its card.
    var nearest = findNearest(geom.location);
    if (nearest) {
      map.setCenter(new google.maps.LatLng(num(nearest.lat), num(nearest.lng)));
      map.setZoom(11); // > cluster maxZoom so the pin isn't hidden inside a cluster
      var m = markers.filter(function (mm) { return mm.__idx === nearest.__idx; })[0];
      if (m) selectMarker(nearest, m);
    } else if (geom.viewport) {
      map.fitBounds(geom.viewport);
    } else {
      map.setCenter(geom.location); map.setZoom(11);
    }
    // The 'idle' listener runs refreshList() once the map settles.
  }

  var geocoder;
  function geocodeSearch() {
    var q = (els.input.value || '').trim();
    if (!q) return;
    if (!geocoder) geocoder = new google.maps.Geocoder();
    geocoder.geocode({ address: q }, function (results, status) {
      if (status === 'OK' && results[0]) applyPlace(results[0].geometry);
    });
  }

  /* ---------------------------------------------------------- script loader */
  function loadScript(src, onload, onerror) {
    var s = document.createElement('script');
    s.src = src;
    s.async = true;
    s.onload = onload;
    s.onerror = onerror || onload;
    document.head.appendChild(s);
  }

  function loadGoogleMaps(key, cb) {
    window.__glMapsReady = cb;
    var url = 'https://maps.googleapis.com/maps/api/js?key=' + encodeURIComponent(key) +
      '&libraries=places,geometry&callback=__glMapsReady';
    loadScript(url, null, function () { initListMode(); }); // maps failed -> degrade
  }

  /* -------------------------------------------------------------------- boot */
  function boot() {
    grab();
    if (!els.wrap) return;
    applyTheme();
    if (DATA.some(hasLatLng) && CFG.mapsKey) loadGoogleMaps(CFG.mapsKey, initMapMode);
    else initListMode();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
